import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CalendarDays, Inbox, ArrowRight } from "lucide-react";
import { API, authHeaders, errorText } from "@/lib/api";
import { useCan } from "@/components/admin/Me";

const WEEK = 7 * 24 * 3600 * 1000;
const COURSE = { llm: "LLM Foundations", prompt: "Prompt Engineering", agentic: "Agentic AI", free: "Free class" };
const TYPES = [["diagnostic", "Diagnostics", "diagnostic"], ["interest", "Course interest", "interest"], ["brochure", "Curriculum downloads", "brochure"],
  ["toolkit", "Toolkit sign-ups", "toolkit"], ["resume", "Resume Studio", "resume"], ["free", "Free class sign-ups", "students"]];
const when = (iso) => new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

const Card = ({ title, action, onAction, children }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-5">
    <div className="mb-3 flex items-center justify-between gap-2"><h2 className="font-display text-[17px] font-bold text-[#0B1B33]">{title}</h2>
      {action && <button onClick={onAction} className="flex items-center gap-1 text-[13px] font-semibold text-[#0E9384]">{action} <ArrowRight className="h-3.5 w-3.5" /></button>}</div>
    {children}
  </section>
);

export const AdminHome = ({ go }) => {
  const can = useCan();
  const leads = can("leads"), learning = can("learning");
  const [people, setPeople] = useState(leads ? null : []);
  const [lms, setLms] = useState(learning ? null : { students: 0, upcoming: [], waiting: [], waiting_total: 0 });
  useEffect(() => {
    const h = { headers: authHeaders() };
    if (leads) axios.get(`${API}/admin/people`, h).then((r) => setPeople(Array.isArray(r.data) ? r.data : [])).catch((e) => { setPeople([]); toast.error(errorText(e)); });
    if (learning) axios.get(`${API}/admin/lms/overview`, h).then((r) => setLms(r.data)).catch(() => setLms({ students: 0, upcoming: [], waiting: [], waiting_total: 0 }));
  }, [leads, learning]);

  const { events, newPeople, byType } = useMemo(() => {
    const since = Date.now() - WEEK;
    const events = (people || []).flatMap((p) => p.events.map((e) => ({ ...e, name: p.name, email: p.email }))).sort((a, b) => b.at.localeCompare(a.at));
    const recent = events.filter((e) => new Date(e.at).getTime() >= since);
    return {
      events,
      newPeople: (people || []).filter((p) => new Date(p.events[p.events.length - 1].at).getTime() >= since).length,
      byType: Object.fromEntries(TYPES.map(([t]) => [t, recent.filter((e) => e.type === t).length])),
    };
  }, [people]);

  if (!people || !lms) return <Loader2 className="mx-auto mt-16 h-8 w-8 animate-spin text-[#0E9384]" />;

  return (
    <div data-testid="admin-home" className="space-y-6">
      {!leads && !learning && <p className="rounded-xl border border-slate-200 bg-white p-5 text-[14px] text-slate-700">Use the menu to open the areas you work on.</p>}
      {leads && <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#0E9384]">Last 7 days</p>
        <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          <button onClick={() => go("people")} data-testid="home-stat-new" className="rounded-xl bg-[#0B1B33] p-4 text-left text-white">
            <p className="text-[13px] text-slate-300">New people</p><p className="font-display text-[30px] font-bold">{newPeople}</p></button>
          {TYPES.map(([t, label, tab]) => (
            <button key={t} onClick={() => go(tab)} data-testid={`home-stat-${t}`} className="rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-[#0E9384]">
              <p className="text-[13px] text-slate-600">{label}</p><p className="font-display text-[30px] font-bold text-[#0B1B33]">{byType[t]}</p></button>
          ))}
        </div>
        <p className="mt-2 text-[12px] text-slate-500">Ad clicks and cost are in Google Ads; these are the sign-ups they turned into on the site.</p>
      </div>}

      <div className={`grid gap-6 ${leads && learning ? "lg:grid-cols-[1.4fr_1fr]" : ""}`}>
        {leads && <Card title="Recent activity" action="All people" onAction={() => go("people")}>
          {events.length ? <ul className="divide-y divide-slate-100">{events.slice(0, 10).map((e, i) => (
            <li key={i} className="flex gap-3 py-2.5 text-[14px]">
              <span className="w-24 shrink-0 text-[12px] text-slate-500">{when(e.at)}</span>
              <span className="min-w-0"><b className="text-[#0B1B33]">{e.name}</b> <span className="text-slate-700">{e.title}</span></span>
            </li>))}</ul>
            : <p className="py-6 text-center text-slate-600">No activity yet.</p>}
        </Card>}

        {learning && <div className="space-y-6">
          <Card title={`Projects to review (${lms.waiting_total})`} action="Open projects" onAction={() => go("projects")}>
            {lms.waiting.length ? <ul className="space-y-2">{lms.waiting.slice(0, 5).map((s) => (
              <li key={s.id} className="flex items-start gap-2 text-[14px]"><Inbox className="mt-0.5 h-4 w-4 shrink-0 text-[#0E9384]" />
                <span><b className="text-[#0B1B33]">{s.student_name}</b> · {s.assignment_title}<span className="block text-[12px] text-slate-500">{COURSE[s.course]} · {when(s.submitted_at)}</span></span></li>))}</ul>
              : <p className="text-[14px] text-slate-600">Nothing waiting.</p>}
          </Card>
          <Card title="Upcoming live sessions" action="Sessions" onAction={() => go("sessions")}>
            {lms.upcoming.length ? <ul className="space-y-2">{lms.upcoming.map((s) => (
              <li key={s.id} className="flex items-start gap-2 text-[14px]"><CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-[#0E9384]" />
                <span><b className="text-[#0B1B33]">{COURSE[s.course]} · S{s.n}</b> {s.title}<span className="block text-[12px] text-slate-500">{s.batch ? `${s.batch} · ` : ""}{when(s.starts_at)}{s.zoom_link ? "" : " · no Zoom link yet"}</span></span></li>))}</ul>
              : <p className="text-[14px] text-slate-600">No sessions scheduled. Add dates in Sessions.</p>}
            <p className="mt-3 border-t border-slate-100 pt-3 text-[13px] text-slate-600"><b className="text-[#0B1B33]">{lms.students}</b> active students</p>
          </Card>
        </div>}
      </div>
    </div>
  );
};
